import GeckoRequest from 'services/gecko-request';
import { reduxStore } from 'services/gecko-store/redux';
import GeckoSocket from '../../services/gecko-socket';
import teamFiltering from '../../services/team-filtering';
import { GeckoEngage } from '../../services/gecko-engage';
import GeckoAuth from '../../services/gecko-auth';
import { GeckoIntegrationsFactory } from '../../services/gecko-integrations';

const dispatch = (event, payload = {}, metadata = {}) => {
  return reduxStore.dispatch({
    type: event,
    payload,
    metadata,
  });
};

const fetchWrapper = (eventPostfix, fetch, addToExisting) => {
  dispatch(`STARTED_FETCHING_${eventPostfix}`);
  return new Promise((resolve, reject) => {
    fetch
      .then((payload) => {
        const { nextPage, page, sort, data, perPage, quickFilter } = payload;
        const event = addToExisting
          ? `FETCHED_MORE_${eventPostfix}`
          : `FETCHED_${eventPostfix}`;
        dispatch(event, { data, page, nextPage, sort, perPage, quickFilter });
        return resolve(data);
      })
      .catch((err) => {
        dispatch(`FAILED_FETCHING_${eventPostfix}`);
        return reject(err);
      });
  });
};

const encodeQuickFilter = (quickFilter) => {
  if (quickFilter === null) {
    return null;
  }
  return JSON.stringify(quickFilter);
};

export const fetchFilter = (eventPostfix, endpointURL) => {
  return ({
    page = 1,
    sort = 'DESC',
    addToExisting = false,
    quickFilter = null,
  } = {}) => {
    return fetchWrapper(
      eventPostfix,
      GeckoRequest.get(endpointURL, {
        page,
        sort,
        quickFilter: encodeQuickFilter(quickFilter),
      }),
      addToExisting
    );
  };
};

export const fetchMyConversations = fetchFilter(
  'MY_CONVERSATIONS',
  '/conversation/my-conversations'
);

export const fetchUnassignedConversations = fetchFilter(
  'UNASSIGNED_CONVERSATIONS',
  '/conversation/unassigned'
);

export const fetchOpenConversations = fetchFilter(
  'OPEN_CONVERSATIONS',
  '/conversation/open'
);

export const fetchClosedConversations = fetchFilter(
  'CLOSED_CONVERSATIONS',
  '/conversation/closed'
);

export const fetchSentConversations = fetchFilter(
  'SENT_CONVERSATIONS',
  '/conversation/sent'
);

export const fetchBotInterferenceConversations = fetchFilter(
  'BOT_INTERFERENCE_CONVERSATIONS',
  '/conversation/bot-interference'
);

export const fetchUndeliveredMessagesConversations = fetchFilter(
  'UNDELIVERED_MESSAGES_CONVERSATIONS',
  '/conversation/failed-messages'
);

const userCanSeeConversation = (
  visibleChannels,
  allAssignedAgents = [],
  lastMessageChannel,
  userId
) =>
  visibleChannels.includes(lastMessageChannel) ||
  allAssignedAgents.includes(userId);

export const updateConversation = (conversation = {}) => {
  const state = reduxStore.getState();
  const channels = state.channels;
  const channelIdsForUser = teamFiltering
    .channelsForUsersTeams(channels)
    .map(({ id }) => id);
  const userId = state.appState.loggedInUser.id;
  let oldConversation =
    state.conversationsRoute.conversationById[conversation.id];

  const oldAssigned = oldConversation?.all_assigned_agent_ids || [];
  const bothAssigned = new Set([
    ...oldAssigned,
    ...conversation.all_assigned_agent_ids,
  ]);
  if (
    !userCanSeeConversation(
      channelIdsForUser,
      [...bothAssigned],
      conversation?.last_message_channel,
      userId
    )
  ) {
    return;
  }

  // If we don't have the conversation but it is assigned to us or is unassigned add it to the store
  if (
    !oldConversation &&
    (!conversation.all_assigned_agent_ids.length ||
      conversation.all_assigned_agent_ids.includes(userId))
  ) {
    dispatch('CREATED_CONVERSATION', conversation, { oldConversation });
    oldConversation = {
      all_assigned_agent_ids: [],
      assigned_agent_ids: [],
      assigned_team_ids: [],
      unread_count: 0,
    };
  } else {
    // If we don't have the convresation to update or the conversation we have is more up to date return early
    if (
      !oldConversation ||
      new Date(oldConversation.updatedAt) > new Date(conversation.updatedAt)
    ) {
      return;
    }
  }
  if (oldConversation.status !== conversation.status) {
    const assignedToMe = conversation.all_assigned_agent_ids.includes(
      state.appState.loggedInUser.id
    );
    dispatch(
      `CONVERSATION_STATUS_CHANGED_TO_${conversation.status.toUpperCase()}`,
      conversation,
      { assignedToMe }
    );
  }
  // If the conversations unread count changed update the unreadConversations
  if (oldConversation.unread_count !== conversation.unread_count) {
    if (conversation.unread_count === 0) {
      dispatch(`CONVERSATION_READ`, { id: conversation.id });
    }
    if (oldConversation.unread_count === 0) {
      dispatch(`CONVERSATION_UNREAD`, { id: conversation.id });
    }
  }
  // If the conversation isn't closed/sent and the agent assignment has changed
  if (
    oldConversation &&
    conversation.status !== 'closed' &&
    conversation.status !== 'no reply' &&
    String(oldConversation.all_assigned_agent_ids) !==
      String(conversation.all_assigned_agent_ids)
  ) {
    if (!conversation.all_assigned_agent_ids.length) {
      // If it's now unassigned dispatch an Event for that
      dispatch('CONVERSATION_UNASSIGNED', conversation);
    }
    if (
      conversation.all_assigned_agent_ids.length &&
      !oldConversation.all_assigned_agent_ids.length
    ) {
      // If it's now assigned dispatch an Event for that
      dispatch('CONVERSATION_ASSIGNED', conversation);
    }
    const isNowAssignedMe = conversation.all_assigned_agent_ids.includes(
      state.appState.loggedInUser.id
    );
    const wasAssignedMe =
      oldConversation &&
      oldConversation.all_assigned_agent_ids.includes(
        state.appState.loggedInUser.id
      );
    if (isNowAssignedMe && !wasAssignedMe) {
      // If it's now assigned to us then dispatch an event for that
      dispatch('CONVERSATION_ASSIGNED_TO_ME', conversation);
    }
    if (wasAssignedMe && !isNowAssignedMe) {
      // If it's been unassigned from us then dispatch an event for that
      dispatch('CONVERSATION_UNASSIGNED_FROM_ME', conversation);
    }
  }
  // If a bot has picked up the conversation then dispatch an event for that
  if (!oldConversation.bot_id && conversation.bot_id) {
    dispatch('CONVERSATION_PICKED_UP_BY_BOT', conversation);
  }
  // If a bot has dropped the conversation then dispatch an event for that
  if (oldConversation.bot_id && !conversation.bot_id) {
    dispatch('CONVERSATION_DROPPED_BY_BOT', conversation);
  }

  if (
    !state.conversationsRoute.conversationById[conversation.id] &&
    state.appState.loggedInUser.permissions.restrict_conversations
  ) {
    return;
  }
  return dispatch('UPDATED_CONVERSATION', conversation, { oldConversation });
};

export const newConversation = (conversation) => {
  if (!conversation) {
    return;
  }
  const state = reduxStore.getState();
  const channels = state.channels;
  const channelIdsForUser = teamFiltering
    .channelsForUsersTeams(channels)
    .map(({ id }) => id);
  if (!channelIdsForUser.includes(conversation.last_message_channel)) {
    return;
  }

  // We don't want to handle "Sent" conversations
  if (conversation.status === 'no reply') {
    return;
  }
  return dispatch('CREATED_CONVERSATION', conversation);
};

export const fetchConversation = async (conversationId) => {
  if (!conversationId) {
    return;
  }
  const state = reduxStore.getState().conversationsRoute;
  if (state.fetchingConversations[conversationId] === true) {
    return;
  }
  dispatch('STARTED_FETCHING_CONVERSATION', conversationId);
  const conversation = await GeckoRequest.get(
    `/conversation/${conversationId}`
  );
  dispatch('FETCHED_CONVERSATION', conversation);
};

let contactsToGet = [];
let bulkFetch = null;
const contactFetchBundler = (contact_id) => {
  if (!bulkFetch) {
    bulkFetch = new Promise((resolve) => {
      setTimeout(() => {
        dispatch('STARTED_FETCHING_CONTACTS', { contacts: contactsToGet });
        GeckoRequest.post('/contact/bulk', { contacts: contactsToGet })
          .then((results) => {
            resolve(results);
            return results;
          })
          .then((results) => {
            dispatch('FETCHED_CONTACTS', { contacts: results });
          });
        bulkFetch = null;
        contactsToGet = [];
      }, 100);
    });
  }

  contactsToGet.push(contact_id);
  return bulkFetch.then((results) => {
    const contact = results[contact_id];
    GeckoSocket.emit('join-contact-update-room', { contactId: contact.id });
    return contact;
  });
};

export const fetchContact = (contact_id) => {
  if (!contact_id) {
    return;
  }
  const state = reduxStore.getState();
  const fetchStatus = state.conversationsRoute.contactsFetchStatus[contact_id];
  // If it's already being fetched don't try again
  if (fetchStatus && fetchStatus.fetching) {
    return;
  }
  contactFetchBundler(contact_id).catch((err) => {
    dispatch('FAILED_FETCHING_CONTACT', { id: contact_id });
  });
};

export const fetchContactNotes = (contactId) => {
  if (!contactId) {
    return;
  }
  const state = reduxStore.getState();
  const fetchStatus = state.conversationsRoute.notesFetchStatus[contactId];
  // If it's already being fetched don't try again
  if (fetchStatus && fetchStatus.fetching) {
    return;
  }
  dispatch('STARTED_FETCHING_CONTACT_NOTES', { contactId });
  return GeckoRequest.get(`/note/by-contact/${contactId}`)
    .then((notes) => {
      dispatch('FETCHED_CONTACT_NOTES', { contactId, notes });
      return notes;
    })
    .catch((err) => {
      dispatch('FAILED_FETCHING_CONTACT_NOTES', { contactId });
      throw err;
    });
};

const getNewIntegrationSyncs = async (contactUuid, conversationUuid) => {
  const authCallback = { getAuthTokenCallback: GeckoAuth.getToken };
  const geckoIntegrationsFactory = GeckoIntegrationsFactory(authCallback);
  const integrationsResponse = await geckoIntegrationsFactory.getIntegrations();
  const syncResponses = (
    await Promise.all(
      integrationsResponse.data.map((integration) => {
        const promises = [];
        promises.push(
          geckoIntegrationsFactory.getContactSyncDetails(
            integration.integrationId,
            contactUuid
          )
        );

        if (conversationUuid) {
          promises.push(
            geckoIntegrationsFactory.getConversationSyncDetails(
              integration.integrationId,
              conversationUuid
            )
          );
        }
        return Promise.all(promises);
      })
    )
  ).flat();

  return {
    errors: syncResponses
      .map((syncResponse) =>
        syncResponse.errors.filter((error) => error.code !== 404)
      )
      .flat(),
    syncs: syncResponses
      .filter((syncResponse) => syncResponse.data)
      .map((syncResponse) => {
        const data = syncResponse.data;
        data.connectionName = integrationsResponse.data.find(
          (integration) => integration.integrationId === data.integrationId
        ).secondaryConnectionConfig.connectionName;
        return data;
      }),
  };
};

export const fetchContactIntegrationSyncs = async (
  contactUuid,
  conversationUuid
) => {
  if (!contactUuid) {
    return;
  }
  const state = reduxStore.getState();
  const fetchStatus =
    state.conversationsRoute.integrationSyncFetchStatus[contactUuid];
  // If it's already being fetched don't try again
  if (fetchStatus && fetchStatus.fetching) {
    return;
  }
  dispatch('STARTED_FETCHING_CONTACT_INTEGRATION_SYNCS', { contactUuid });
  try {
    const syncs = await getNewIntegrationSyncs(contactUuid, conversationUuid);
    dispatch('FETCHED_CONTACT_INTEGRATION_SYNCS', { contactUuid, syncs });
  } catch (err) {
    dispatch('FAILED_FETCHING_CONTACT_INTEGRATION_SYNCS', { contactUuid });
    console.error(err);
  }
};

export const updateContact = (contact) => {
  if (!contact) {
    return;
  }
  const state = reduxStore.getState();
  if (!state.conversationsRoute.contactsById[contact.id]) {
    return;
  }
  dispatch('UPDATED_CONTACT', contact);
};

const fetchContactFilterBase = (
  { contactId, page = 1, addToExisting = false, quickFilter = null },
  eventStub,
  url,
  stateObject
) => {
  const state = reduxStore.getState();
  contactId = contactId || state.conversationsRoute[stateObject].contact;
  dispatch(`STARTED_FETCHING_${eventStub}_FILTER`, { contactId });
  return GeckoRequest.get(`/conversation/${url}`, {
    contact: contactId,
    page,
    quickFilter: encodeQuickFilter(quickFilter),
  })
    .then((payload) => {
      const { nextPage, page, data, quickFilter } = payload;
      const event = addToExisting
        ? `FETCHED_MORE_${eventStub}_FILTER`
        : `FETCHED_${eventStub}_FILTER`;
      dispatch(event, { data, page, nextPage, quickFilter });
      return payload;
    })
    .catch((err) => {
      dispatch(`FAILED_FETCHING_${eventStub}_FILTER`, {
        contactId,
        error: err,
      });
    });
};

export const fetchContactFilter = (payload) =>
  fetchContactFilterBase(
    payload,
    'CONTACT',
    'by-contact-open',
    'contactFilter'
  );

export const fetchAllForContactFilter = (payload) =>
  fetchContactFilterBase(
    payload,
    'ALL_FOR_CONTACT',
    'by-contact',
    'allForContactFilter'
  );

export const fetchTagFilter = ({
  tagId,
  page = 1,
  addToExisting = false,
  quickFilter = null,
}) => {
  const state = reduxStore.getState();
  tagId = tagId || state.conversationsRoute.tagFilter.tag;
  dispatch('STARTED_FETCHING_TAG_FILTER', { tagId });
  return GeckoRequest.get('/conversation/by-tag', {
    tag: tagId,
    page,
    quickFilter: encodeQuickFilter(quickFilter),
  })
    .then((payload) => {
      const { nextPage, page, data, quickFilter } = payload;
      const event = addToExisting
        ? 'FETCHED_MORE_TAG_FILTER'
        : 'FETCHED_TAG_FILTER';
      dispatch(event, { data, page, nextPage, quickFilter });
      return payload;
    })
    .catch((err) => {
      dispatch('FAILED_FETCHING_TAG_FILTER', { tagId, error: err });
    });
};

const sendSavedCustomFilterPinEvent = (pinnedFilterIds) =>
  GeckoRequest.post('/conversation/custom-filter/pin-event', {
    pinnedFilterIds,
  }).catch((error) => console.error(error));

const getPinnedFiltersFromStorage = () => {
  const savedFilters = localStorage.getItem(
    'conversation-saved-custom-filters'
  );
  if (!savedFilters) return [];
  return JSON.parse(savedFilters);
};

const setPinnedFiltersInStorage = (filters) => {
  localStorage.setItem(
    'conversation-saved-custom-filters',
    JSON.stringify(filters)
  );
};

export const pinSavedCustomFilter = (id) => {
  const currentFilters = getPinnedFiltersFromStorage();
  if (currentFilters.includes(id)) return;

  const updatedFilters = [...currentFilters, id];

  setPinnedFiltersInStorage(updatedFilters);
  sendSavedCustomFilterPinEvent(updatedFilters);
  return dispatch('PINNED_SAVED_CUSTOM_FILTER', { id });
};

export const unpinSavedCustomFilter = (id) => {
  const currentFilters = getPinnedFiltersFromStorage();
  if (!currentFilters.includes(id)) return;

  const filteredFilters = currentFilters.filter((filterId) => filterId !== id);

  setPinnedFiltersInStorage(filteredFilters);
  sendSavedCustomFilterPinEvent(filteredFilters);
  return dispatch('UNPINNED_SAVED_CUSTOM_FILTER', { id });
};

export const fetchSavedCustomFiltersDetails = async () => {
  return GeckoRequest.get('/conversation/custom-filter/agent').then(
    (savedCustomFilters) => {
      dispatch('FETCHED_SAVED_CUSTOM_FILTERS_DETAILS', savedCustomFilters);
    }
  );
};

export const createSavedCustomFilter = async (
  customFilterDetails,
  pinFilter
) => {
  return GeckoRequest.post(
    '/conversation/custom-filter',
    customFilterDetails
  ).then(async (createdFilter) => {
    dispatch('CREATED_SAVED_CUSTOM_FILTER', createdFilter);
    if (pinFilter) {
      pinSavedCustomFilter(createdFilter.id);
    }
    await fetchSavedCustomFilter({
      id: createdFilter.id,
      filters: createdFilter.config,
    });
    await fetchSavedCustomFilterCount({
      id: createdFilter.id,
      filters: createdFilter.config,
    });
  });
};

export const updateSavedCustomFilter = async (
  filterId,
  updateDetails,
  pinFilter
) => {
  return GeckoRequest.post(
    `/conversation/custom-filter/${filterId}`,
    updateDetails
  ).then(async (updatedFilter) => {
    dispatch('UPDATED_SAVED_CUSTOM_FILTER', updatedFilter);
    if (pinFilter) {
      pinSavedCustomFilter(updatedFilter.id);
    } else {
      unpinSavedCustomFilter(updatedFilter.id);
    }
    await fetchSavedCustomFilter({
      id: filterId,
      filters: updatedFilter.config,
    });
    await fetchSavedCustomFilterCount({
      id: filterId,
      filters: updatedFilter.config,
    });
  });
};

export const deleteSavedCustomFilter = async (filterId) => {
  return GeckoRequest.delete(`/conversation/custom-filter/${filterId}`).then(
    () => {
      dispatch('DELETED_SAVED_CUSTOM_FILTER', filterId);
    }
  );
};

export const fetchCustomFilter = ({
  filters,
  page = 1,
  sort = 'DESC',
  addToExisting = false,
  quickFilter = null,
} = {}) => {
  const state = reduxStore.getState();
  filters = filters || state.conversationsRoute.customFilter.filters;
  dispatch('STARTED_FETCHING_CUSTOM_FILTER', { filters });
  return GeckoRequest.get('/conversation/custom-filter', {
    filters: JSON.stringify(filters),
    page,
    sort,
    quickFilter: encodeQuickFilter(quickFilter),
  })
    .then((payload) => {
      const { nextPage, page, sort, data, quickFilter } = payload;
      const event = addToExisting
        ? 'FETCHED_MORE_CUSTOM_FILTER'
        : 'FETCHED_CUSTOM_FILTER';
      dispatch(event, { data, page, nextPage, sort, quickFilter });
      localStorage.setItem(
        'conversation-custom-filter',
        JSON.stringify(filters)
      );
      return payload;
    })
    .catch((err) => {
      dispatch('FAILED_FETCHING_CUSTOM_FILTER', { filters, error: err });
    });
};

export const fetchSavedCustomFilter = ({
  id,
  filters,
  page = 1,
  sort = 'DESC',
  addToExisting = false,
  quickFilter = null,
}) => {
  dispatch('STARTED_FETCHING_SAVED_CUSTOM_FILTER', { id, filters });
  return GeckoRequest.get('/conversation/custom-filter', {
    filters: JSON.stringify(filters),
    page,
    sort,
    quickFilter: encodeQuickFilter(quickFilter),
  })
    .then((payload) => {
      const { nextPage, page, sort, data, quickFilter } = payload;
      const event = addToExisting
        ? 'FETCHED_MORE_SAVED_CUSTOM_FILTER'
        : 'FETCHED_SAVED_CUSTOM_FILTER';
      dispatch(event, { id, data, page, nextPage, sort, quickFilter });
      return payload;
    })
    .catch((err) => {
      dispatch('FAILED_FETCHING_SAVED_CUSTOM_FILTER', {
        id,
        filters,
        error: err,
      });
    });
};

export const fetchAllSavedCustomFilters = () => {
  const { savedCustomFilters = [] } = reduxStore.getState().conversationsRoute;
  return Promise.all(
    savedCustomFilters.map(({ id, filters }) => {
      return fetchSavedCustomFilter({ id, filters });
    })
  );
};

const fetchCountWrapper = (urlSlug, eventSlug, params = {}, id) => {
  return GeckoRequest.get(`/conversation/${urlSlug}/count`, params)
    .then((count) => {
      dispatch(`FETCHED_${eventSlug}_COUNT`, { count: Number(count), id });
    })
    .catch((err) => {
      console.error(err);
    });
};

export const fetchContactConversationCount = (contact) =>
  GeckoRequest.get(`/conversation/by-contact-open/count`, {
    open: true,
    contact,
  })
    .then((count) => {
      dispatch(`FETCHED_CONTACT_CONVERSATIONS_COUNT`, {
        [String(contact)]: Number(count),
      });
    })
    .catch((err) => {
      console.error(err);
    });

export const fetchMyConversationsCount = () =>
  fetchCountWrapper('my-conversations', 'MY_CONVERSATIONS');
export const fetchUnassignedConversationsCount = () =>
  fetchCountWrapper('unassigned', 'UNASSIGNED_CONVERSATIONS');
export const fetchOpenConversationsCount = () =>
  fetchCountWrapper('open', 'OPEN_CONVERSATIONS');
export const fetchBotInterferenceConversationsCount = () =>
  fetchCountWrapper('bot-interference', 'BOT_INTERFERENCE_CONVERSATIONS');
export const fetchCustomFilterCount = ({ filters } = {}) => {
  const state = reduxStore.getState();
  filters = filters || state.conversationsRoute.customFilter.filters;
  return fetchCountWrapper('custom-filter', 'CUSTOM_FILTER', {
    filters: JSON.stringify(filters),
  });
};
export const fetchSavedCustomFilterCount = ({ filters, id }) => {
  return fetchCountWrapper(
    'custom-filter',
    'SAVED_CUSTOM_FILTER',
    {
      filters: JSON.stringify(filters),
    },
    id
  );
};
export const fetchAllSavedCustomFilterCounts = () => {
  const { savedCustomFilters } = reduxStore.getState().conversationsRoute;
  return Promise.all(
    savedCustomFilters.map(({ id, filters }) =>
      fetchSavedCustomFilterCount({ id, filters })
    )
  );
};
export const fetchUndeliveredMessagesConversationsCount = () =>
  fetchCountWrapper('failed-messages', 'UNDELIVERED_MESSAGES_CONVERSATIONS');
export const fetchConversationCounts = () => {
  return Promise.all([
    fetchMyConversationsCount(),
    fetchUnassignedConversationsCount(),
    fetchOpenConversationsCount(),
    fetchBotInterferenceConversationsCount(),
    fetchCustomFilterCount(),
    fetchUndeliveredMessagesConversationsCount(),
    fetchAllSavedCustomFilterCounts(),
  ]);
};

export const handleMergedContacts = ({ oldContactId, newContactId }) => {
  return dispatch('CONTACTS_MERGED', { oldContactId, newContactId });
};

export const fetchContactAttendances = async (contactUlid) => {
  if (!contactUlid) {
    return;
  }

  const state = reduxStore.getState();
  const fetchStatus =
    state.conversationsRoute.attendanceFetchStatus[contactUlid];

  if (fetchStatus && fetchStatus.fetching) {
    return;
  }

  try {
    let endpoint = `contacts/${contactUlid}?include=attendances.event&contact_rfields=contactId&attendance_rfields=rsvp,status_title,created_at&event_rfields=title,start_datetime,end_datetime,parent_id`;
    dispatch('STARTED_FETCHING_CONTACT_ATTENDANCES', { contactUlid });
    const authCallback = { getAuthTokenCallback: GeckoAuth.getToken };
    const { contact } = await GeckoEngage(authCallback).fetchData(endpoint);
    const { attendances } = contact;
    dispatch('FETCHED_CONTACT_ATTENDANCES', { contactUlid, attendances });
    return attendances;
  } catch (e) {
    dispatch('FAILED_FETCHING_CONTACT_ATTENDANCES', { contactUlid });
    console.error(e);
  }
};
